import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import logo from "../assets/logo.png";
import API_BASE_URL from "../config";
import AnnouncementPopup from "../announcement/AnnouncementPopup";
import {
  FaHome,
  FaSearch,
  FaSlidersH,
  FaSignOutAlt,
  FaUserCog,
  FaChevronDown,
  FaExchangeAlt,
} from "react-icons/fa";

/* ─────────────── styles ─────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap');

  .mnav * { font-family: 'DM Sans', sans-serif; box-sizing: border-box; }

  /* ── top bar ── */
  .mnav-topbar {
    background: #ffffff;
    border-bottom: 1px solid #eef0f4;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 8px;
    height: 58px;
    position: relative;
    z-index: 1100;
  }

  /* ── main navbar ── */
  .mnav-bar {
    height: 48px;
    display: flex;
    align-items: center;
    padding: 0 24px;
    position: relative;
    z-index: 1050;
    transition: background-color 0.3s ease;
  }
  .mnav-bar::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 1px;
    background: rgba(255,255,255,0.08);
  }

  /* ── nav item ── */
  .mnav-item {
    position: relative;
    display: flex;
    align-items: center;
    gap: 5px;
    padding: 0 14px;
    height: 46px;
    color: #ffffff;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.2px;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.18s ease;
    white-space: nowrap;
    user-select: none;
  }
  .mnav-item:hover { color: #ffffff; }
  .mnav-item::before {
    content: '';
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: rgba(255,255,255,0);
    border-radius: 2px 2px 0 0;
    transition: background 0.18s ease;
  }
  .mnav-item:hover::before,
  .mnav-item.active::before { background: rgba(255,255,255,0.7); }
  .mnav-item .chevron {
    font-size: 10px;
    opacity: 0.7;
    margin-left: 1px;
    transition: transform 0.2s ease, opacity 0.2s;
  }
  .mnav-item:hover .chevron { transform: rotate(180deg); opacity: 1; }

  /* ── dropdown panel ── */
  .mnav-drop {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    min-width: 190px;
    background: #ffffff;
    border: 1px solid #e8eaf0;
    border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible;
    animation: mdropIn 0.17s ease;
    z-index: 2000;
  }
  @keyframes mdropIn {
    from { opacity: 0; transform: translateY(-6px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes mflyIn {
    from { opacity: 0; transform: translateX(-6px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  .mnav-drop > a:first-child,
  .mnav-drop > .mnav-flyout-wrap:first-child > .mnav-flyout-row { border-radius: 10px 10px 0 0; }
  .mnav-drop > a:last-child,
  .mnav-drop > .mnav-flyout-wrap:last-child > .mnav-flyout-row  { border-radius: 0 0 10px 10px; }
  .mnav-drop > a:only-child { border-radius: 10px; }
  .mnav-drop a {
    display: block;
    padding: 9px 16px;
    font-size: 13px;
    font-weight: 500;
    color: #2d3142;
    text-decoration: none;
    transition: background 0.13s, color 0.13s;
    border-left: 3px solid transparent;
    cursor: pointer;
  }
  .mnav-drop a:hover {
    background: #f5f6ff;
    color: #080235;
    border-left-color: #5b5fc7;
  }

  /* ── flyout (level 2) ── */
  .mnav-flyout-wrap { position: relative; }
  .mnav-flyout-wrap > .mnav-flyout-row {
    display: flex; align-items: center; justify-content: space-between;
    padding: 9px 16px; font-size: 13px; font-weight: 500; color: #2d3142;
    cursor: pointer; border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap; user-select: none;
  }
  .mnav-flyout-wrap:hover > .mnav-flyout-row,
  .mnav-flyout-wrap.open > .mnav-flyout-row {
    background: #f5f6ff; color: #080235; border-left-color: #5b5fc7;
  }
  .mnav-flyout-wrap > .mnav-flyout-row .fly-arrow { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  .mnav-flyout-panel {
    position: absolute; top: -1px; left: 100%; min-width: 200px;
    background: #ffffff; border: 1px solid #e8eaf0; border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: visible; animation: mflyIn 0.15s ease; z-index: 3000;
  }
  .mnav-flyout-panel > a:first-child,
  .mnav-flyout-panel > .mnav-flyout-wrap2:first-child > .mnav-flyout-row2 { border-radius: 10px 10px 0 0; }
  .mnav-flyout-panel > a:last-child,
  .mnav-flyout-panel > .mnav-flyout-wrap2:last-child > .mnav-flyout-row2  { border-radius: 0 0 10px 10px; }
  .mnav-flyout-panel > a:only-child { border-radius: 10px; }
  .mnav-flyout-panel > a {
    display: block; padding: 9px 16px; font-size: 13px; font-weight: 500;
    color: #2d3142; text-decoration: none; border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .mnav-flyout-panel > a:hover { background: #f5f6ff; color: #080235; border-left-color: #5b5fc7; }

  /* ── flyout (level 3) ── */
  .mnav-flyout-wrap2 { position: relative; }
  .mnav-flyout-wrap2 > .mnav-flyout-row2 {
    display: flex; align-items: center; justify-content: space-between;
    padding: 9px 16px; font-size: 13px; font-weight: 500; color: #2d3142;
    cursor: pointer; border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
    white-space: nowrap; user-select: none;
  }
  .mnav-flyout-wrap2:hover > .mnav-flyout-row2,
  .mnav-flyout-wrap2.open > .mnav-flyout-row2 {
    background: #f5f6ff; color: #080235; border-left-color: #5b5fc7;
  }
  .mnav-flyout-wrap2 > .mnav-flyout-row2 .fly-arrow2 { font-size: 14px; opacity: 0.5; margin-left: 14px; }

  .mnav-flyout-panel2 {
    position: absolute; top: -1px; left: 100%; min-width: 160px;
    background: #ffffff; border: 1px solid #e8eaf0; border-radius: 10px;
    box-shadow: 0 12px 36px rgba(8,2,53,0.13), 0 2px 8px rgba(8,2,53,0.07);
    overflow: hidden; animation: mflyIn 0.15s ease; z-index: 4000;
  }
  .mnav-flyout-panel2 a {
    display: block; padding: 9px 16px; font-size: 13px; font-weight: 500;
    color: #2d3142; text-decoration: none; border-left: 3px solid transparent;
    transition: background 0.13s, color 0.13s;
  }
  .mnav-flyout-panel2 a:hover { background: #f5f6ff; color: #080235; border-left-color: #5b5fc7; }

  /* ── search ── */
  .mnav-search {
    display: flex;
    align-items: center;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    border-radius: 22px;
    padding: 0 14px;
    height: 32px;
    gap: 8px;
    transition: background 0.2s, border 0.2s, width 0.3s ease;
    width: 180px;
    margin-left: 12px;
  }
  .mnav-search:focus-within {
    background: rgba(255,255,255,0.2);
    border-color: rgba(255,255,255,0.4);
    width: 220px;
  }
  .mnav-search input {
    background: transparent;
    border: none;
    outline: none;
    color: #fff;
    font-size: 13px;
    width: 100%;
  }
  .mnav-search input::placeholder { color: rgba(255,255,255,0.55); }
  .mnav-search svg { color: rgba(255,255,255,0.6); flex-shrink: 0; }

  /* ── profile btn ── */
  .mnav-profile-btn {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    padding: 6px 10px;
    border-radius: 10px;
    transition: background 0.15s;
  }
  .mnav-profile-btn:hover { background: #f4f5fb; }
  .mnav-avatar {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    object-fit: cover;
    flex-shrink: 0;
  }
  .mnav-avatar-fallback {
    width: 36px; height: 36px;
    border-radius: 50%;
    border: 2px solid #e8eaf0;
    background: #e8eaf0;
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 14px; color: #374151;
    flex-shrink: 0;
  }
  .mnav-profile-name { font-size: 14px; font-weight: 600; color: #1a1d2e; line-height: 1.2; }
  .mnav-profile-role { font-size: 11px; font-weight: 500; color: #8b90a7; }

  /* ── profile dropdown ── */
  .mnav-profile-drop {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: #fff;
    border: 1px solid #e8eaf0;
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(8,2,53,0.13);
    overflow: hidden;
    animation: mdropIn 0.17s ease;
    z-index: 3000;
  }
  .mnav-profile-header {
    padding: 14px 16px 10px;
    background: linear-gradient(135deg, #f8f9ff 0%, #eef0fb 100%);
    border-bottom: 1px solid #e8eaf0;
  }
  .mnav-profile-header .name { font-size: 14px; font-weight: 700; color: #1a1d2e; }
  .mnav-profile-header .role-badge {
    display: inline-block;
    margin-top: 4px;
    padding: 2px 10px;
    font-size: 10.5px;
    font-weight: 600;
    border-radius: 20px;
    background: #080235;
    color: #fff;
    letter-spacing: 0.4px;
    text-transform: uppercase;
  }
  .mnav-profile-drop .menu-item {
    display: flex; align-items: center; gap: 10px;
    padding: 10px 16px;
    font-size: 13px; font-weight: 500;
    color: #2d3142;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.13s;
  }
  .mnav-profile-drop .menu-item:hover { background: #f5f6ff; }
  .mnav-profile-drop .menu-item.danger { color: #dc2626; }
  .mnav-profile-drop .menu-item.danger:hover { background: #fff5f5; }
  .mnav-profile-drop .divider { height: 1px; background: #f0f1f5; margin: 4px 0; }

  /* ── settings / home btns ── */
  .mnav-settings-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 14px;
    border-radius: 8px;
    font-size: 15px; font-weight: 600;
    color: #444b6b;
    cursor: pointer;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .mnav-settings-btn:hover { background: #f0f1f8; color: #080235; }
  .mnav-home-btn {
    display: flex; align-items: center;
    padding: 7px 10px;
    border-radius: 8px;
    color: #6b7394;
    text-decoration: none;
    transition: background 0.15s, color 0.15s;
  }
  .mnav-home-btn:hover { background: #f0f1f8; color: #080235; }

  /* ── role pills ── */
  .role-pill {
    display: inline-block;
    padding: 4px 12px;
    font-size: 11.5px; font-weight: 600;
    border-radius: 20px;
    background: #f0f1f8;
    color: #3a4060;
    cursor: pointer;
    margin: 2px 0;
    transition: background 0.15s, color 0.15s;
    text-decoration: none;
  }
  .role-pill:hover { background: #080235; color: #fff; }
`;

/* ─────────────── static style objects (stable refs — no new object per render) ─────────────── */
const S_FLEX_ROW_16 = { display: "flex", alignItems: "center", gap: 16 };
const S_FLEX_ROW_4 = { display: "flex", alignItems: "center", gap: 4 };
const S_DIVIDER = { width: 1, height: 32, background: "#eef0f4", margin: "0 8px" };
const S_RELATIVE = { position: "relative" };
const S_NAV_FLEX = { display: "flex", alignItems: "center", flex: 1, gap: 2 };
const S_LOGO = { height: 39 };
const S_COMPANY_LOGO = { height: 37, width: "auto", objectFit: "contain", borderRadius: 4, borderLeft: "2px solid #eef0f4", paddingLeft: 16 };
const S_ROLE_SECTION = { padding: "8px 16px" };
const S_ROLE_LABEL = { fontSize: 10.5, fontWeight: 700, color: "#9ea3bb", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 6 };
const S_ROLE_PILLS = { display: "flex", flexWrap: "wrap", gap: 4 };
const S_ICON_07 = { opacity: 0.7 };
const S_EXCH = { marginRight: 5, opacity: 0.7 };

/* ─────────────── sub-components (outside ManagerNavbar to prevent remount on every render) ─────────────── */

const Avatar = React.memo(function Avatar({ photoError, photo, username, setPhotoError }) {
  if (!photoError && photo)
    return <img src={photo} alt="avatar" className="mnav-avatar" onError={() => setPhotoError(true)} />;
  return (
    <div className="mnav-avatar-fallback">
      {(username || "M").charAt(0).toUpperCase()}
    </div>
  );
});

const Level3Panel = React.memo(function Level3Panel({ grandchildren, flyKey2, openFlyout2, navigate, setOpenDropdown, setOpenFlyout, setOpenFlyout2 }) {
  if (openFlyout2 !== flyKey2) return null;
  return (
    <div className="mnav-flyout-panel2">
      {grandchildren.map((gc, k) => (
        <a key={k} href={gc.href} onClick={(e) => {
          e.preventDefault();
          setOpenDropdown(null); setOpenFlyout(null); setOpenFlyout2(null);
          navigate(gc.href);
        }}>
          {gc.label}
        </a>
      ))}
    </div>
  );
});

const Level2Panel = React.memo(function Level2Panel({ items, itemKey, lnkIdx, openFlyout, openFlyout2, keepOpen, navigate, setOpenDropdown, setOpenFlyout, setOpenFlyout2 }) {
  if (openFlyout !== `${itemKey}-${lnkIdx}`) return null;
  return (
    <div className="mnav-flyout-panel" onMouseEnter={keepOpen}>
      {items.map((child, j) => {
        const flyKey2 = `${itemKey}-${lnkIdx}-${j}`;
        if (child.grandchildren) {
          return (
            <div key={j}
              className={`mnav-flyout-wrap2${openFlyout2 === flyKey2 ? " open" : ""}`}
              onMouseEnter={() => { keepOpen(); setOpenFlyout2(flyKey2); }}
              onMouseLeave={() => setOpenFlyout2(null)}
            >
              <div className="mnav-flyout-row2" onClick={() => {
                if (child.href) {
                  setOpenDropdown(null); setOpenFlyout(null); setOpenFlyout2(null);
                  navigate(child.href);
                }
              }}>
                {child.label}
                <span className="fly-arrow2">›</span>
              </div>
              <Level3Panel
                grandchildren={child.grandchildren}
                flyKey2={flyKey2}
                openFlyout2={openFlyout2}
                navigate={navigate}
                setOpenDropdown={setOpenDropdown}
                setOpenFlyout={setOpenFlyout}
                setOpenFlyout2={setOpenFlyout2}
              />
            </div>
          );
        }
        return (
          <a key={j} href={child.href} onClick={(e) => {
            e.preventDefault();
            setOpenDropdown(null); setOpenFlyout(null);
            navigate(child.href);
          }}>
            {child.label}
          </a>
        );
      })}
    </div>
  );
});

/* ─────────────── component ─────────────── */
const ManagerNavbar = () => {
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState(null);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [searchVal, setSearchVal] = useState("");
  const [photoError, setPhotoError] = useState(false);
  const [profile, setProfile] = useState({ username: "Manager", role: "MANAGER", roles: [], photo: null });
  const [navbarColor, setNavbarColor] = useState("#080235");
  const [permissions, setPermissions] = useState({});
  const [openFlyout, setOpenFlyout] = useState(null);
  const [openFlyout2, setOpenFlyout2] = useState(null);
  const closeTimerRef = useRef(null);
  const photoBlobUrlRef = useRef(null);

  const userId = localStorage.getItem("userId");
  const employeeId = localStorage.getItem("employeeId");

  const activeRole = useMemo(
    () => localStorage.getItem("activeRole") || (profile.role ? `ROLE_${profile.role.toUpperCase()}` : null),
    [profile.role]
  );

  /* ── timer cleanup on unmount ── */
  useEffect(() => () => { if (closeTimerRef.current) clearTimeout(closeTimerRef.current); }, []);

  /* ── timer helpers ── */
  const open = useCallback((key) => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    setOpenDropdown(key);
  }, []);

  const close = useCallback(() => {
    closeTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      setOpenFlyout(null);
      setOpenFlyout2(null);
    }, 120);
  }, []);

  const keepOpen = useCallback(() => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  const handleGoToTeamTimesheets = useCallback(() => {
    if (!employeeId) return console.error("Manager ID not set");
    navigate(`/managertimesheetgrid/${employeeId}`);
  }, [navigate, employeeId]);

  const handleGoToTimesheet = useCallback(
    () => navigate("/managertimesheet", { state: { employeeId } }),
    [navigate, employeeId]
  );

  const handleGoToEvaluation = useCallback(
    () => navigate("/manager-evaluation", { state: { employeeId } }),
    [navigate, employeeId]
  );

  /* ── auth check ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in to access this page."); navigate("/"); return; }
    if (!roles.includes("ROLE_MANAGER")) { toast.error("You are not authorized to access this page."); navigate("/"); return; }
    localStorage.setItem("activeRole", "ROLE_MANAGER");
  }, [navigate]);

  /* ── appearance ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const uid = localStorage.getItem("userId");
    if (!token || !uid) return;
    const controller = new AbortController();
    fetch(`${API_BASE_URL}/api/appearance/${uid}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(d => { if (d?.navbarColor) setNavbarColor(d.navbarColor); })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  /* ── company logo ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const controller = new AbortController();
    fetch(`${API_BASE_URL}/api/company-info/logo`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      signal: controller.signal,
    })
      .then(res => res.json())
      .then(data => { if (data.logoPath) setCompanyLogo(`${API_BASE_URL}/${data.logoPath}`); })
      .catch(err => { if (err.name !== "AbortError") console.error(err); });
    return () => controller.abort();
  }, []);

  /* ── permissions ── */
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const empId = localStorage.getItem("employeeId");
        const res = await fetch(`${API_BASE_URL}/api/permissions/${empId}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const data = await res.json();
        setPermissions(data.pages || {});
      } catch (err) { if (err.name !== "AbortError") console.error("Permission fetch error:", err); }
    })();
    return () => controller.abort();
  }, []);

  /* ── profile ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const eid = localStorage.getItem("employeeId");
    if (!token || !eid) return;
    const controller = new AbortController();
    fetch(`${API_BASE_URL}/api/users/employee/${eid}`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.json())
      .then(d => {
        if (d) setProfile(p => ({
          ...p,
          username: d.name || p.username,
          roles: d.roles || ["ROLE_MANAGER"],
          role: d.roles?.includes("ROLE_MANAGER") ? "MANAGER" : p.role,
        }));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [userId]);

  /* ── photo (separate effect so blob URL cleanup is isolated) ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    let mounted = true;
    const controller = new AbortController();

    fetch(`${API_BASE_URL}/api/employees/me/photo`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then(r => r.blob())
      .then(blob => {
        if (!mounted) return;
        if (blob.size > 0) {
          if (photoBlobUrlRef.current) URL.revokeObjectURL(photoBlobUrlRef.current);
          const url = URL.createObjectURL(blob);
          photoBlobUrlRef.current = url;
          setProfile(p => ({ ...p, photo: url }));
        } else {
          setPhotoError(true);
        }
      })
      .catch(err => { if (mounted && err.name !== "AbortError") setPhotoError(true); });

    return () => {
      mounted = false;
      controller.abort();
      if (photoBlobUrlRef.current) {
        URL.revokeObjectURL(photoBlobUrlRef.current);
        photoBlobUrlRef.current = null;
      }
    };
  }, []);

  /* ── nav items (memoized — only recomputes when permissions/deps change) ── */
  const navItems = useMemo(() => [
    {
      key: "tasks", label: "Tasks", landing: "/managertasklanding",
      links: [{ label: "Task Management", href: "/viewtaskss" }],
    },
    {
      key: "timesheets", label: "Timesheets", landing: "/managertimesheetlanding",
      links: [
        { label: "Team Timesheet", onClick: (e) => { e.preventDefault(); handleGoToTeamTimesheets(); } },
        { label: "My Timesheet", onClick: (e) => { e.preventDefault(); handleGoToTimesheet(); } },
        { label: "Team Evaluation", onClick: (e) => { e.preventDefault(); handleGoToEvaluation(); } },
        ...(permissions?.analytics
          ? [{ label: "Overview", onClick: (e) => { e.preventDefault(); navigate("/admintimesheetgrid"); } }]
          : []),
      ],
    },
    {
      key: "leave", label: "Leave", landing: "/managerleavelanding",
      links: [
        { label: "Leave Approvals", href: "/managerteamleaves" },
        { label: "Leave Application", href: "/managerleaveappli" },
        { label: "My Leaves", href: "/managerleaves" },
      ],
    },
    {
      key: "team", label: "Team", landing: "/managerteamlanding",
      links: [{ label: "Team Management", href: "/teampage" }],
    },
    {
      key: "docs", label: "Documents", landing: "/managerdocumentlanding",
      links: [
        { label: "Upload Documents", href: "/manageruploaddocs" },
        { label: "My Documents", href: "/managerdocs" },
      ],
    },
    ...(permissions?.leave_summary
      ? [{
          key: "leave_summary",
          label: "Leave Summary",
          landing: "/leavesummary",
          links: [{ label: "Leave Summary", onClick: (e) => { e.preventDefault(); navigate("/leavesummary"); } }],
        }]
      : []),
    {
      key: "details", label: "Details", landing: "/managerdetailslanding",
      links: [
        { label: "View Details", onClick: (e) => { e.preventDefault(); navigate("/managerviewdetails", { state: { employeeId } }); } },
        { label: "Holiday Calendar", href: "/mholidays" },
      ],
    },
    {
      key: "expenses", label: "Expenses", landing: "/manager-expenses",
      links: [{ label: "Manager Dashboard", href: "/manager-expenses" }],
    },
    {
      key: "assets", label: "Assets", landing: "/manager-assets",
      links: [
        { label: "My Assets", href: "/myassets" },
        { label: "Approval Queue", href: "/asset-approvals" },
      ],
    },
    ...(permissions?.asset_management
      ? [{
        key: "asset mgmt",
        label: "Asset Management",
        landing: "/admin-assets",
        links: [
          {
            label: "Asset Register",
            children: [
              { label: "All Assets", href: "/allassets", grandchildren: [{ label: "New", href: "/addasset" }] },
              { label: "Allocations", href: "/allallocations", grandchildren: [{ label: "New", href: "/newallocation" }] },
              { label: "Transfers", href: "/alltransfers", grandchildren: [{ label: "New", href: "/newtransfer" }] },
              { label: "Maintenance", href: "/allmaintenance", grandchildren: [{ label: "New", href: "/newmaintenance" }] },
              { label: "Audit", href: "/allauditrecords", grandchildren: [{ label: "New", href: "/newauditrecord" }] },
              { label: "Field Master", href: "/asset-field-master" },
              { label: "Reports", href: "/asset-reports" },
              { label: "Vendors", href: "/vendors", grandchildren: [{ label: "New", href: "/addvendor" }] },
            ],
          },
        ],
      }]
      : []),
    {
      key: "alerts", label: "Alerts", landing: "/alertslanding",
      links: [
        ...(permissions?.announcement
          ? [{ label: "Announcement", onClick: (e) => { e.preventDefault(); navigate("/announcement", { state: { employeeId } }); } }]
          : []),
      ],
    },
    {
      key: "evaluation", label: "Evaluation", landing: "/evaluation",
      links: [
        ...(permissions?.employee_evaluation
          ? [{ label: "Employee Evaluation", onClick: (e) => { e.preventDefault(); navigate("/evaluation"); } }]
          : []),
      ],
    },
  ], [permissions, employeeId, navigate, handleGoToTeamTimesheets, handleGoToTimesheet, handleGoToEvaluation]);

  /* ─────────── render helpers ─────────── */

  const renderLinks = (item) =>
    item.links.map((lnk, i) => {
      if (lnk.children) {
        const flyKey = `${item.key}-${i}`;
        return (
          <div key={i}
            className={`mnav-flyout-wrap${openFlyout === flyKey ? " open" : ""}`}
            onMouseEnter={() => { keepOpen(); setOpenFlyout(flyKey); setOpenFlyout2(null); }}
            onMouseLeave={() => { setOpenFlyout(null); setOpenFlyout2(null); }}
          >
            <div className="mnav-flyout-row" onClick={() => {
              if (lnk.href) { setOpenDropdown(null); setOpenFlyout(null); setOpenFlyout2(null); navigate(lnk.href); }
            }}>
              {lnk.label}
              <span className="fly-arrow">›</span>
            </div>
            <Level2Panel
              items={lnk.children}
              itemKey={item.key}
              lnkIdx={i}
              openFlyout={openFlyout}
              openFlyout2={openFlyout2}
              keepOpen={keepOpen}
              navigate={navigate}
              setOpenDropdown={setOpenDropdown}
              setOpenFlyout={setOpenFlyout}
              setOpenFlyout2={setOpenFlyout2}
            />
          </div>
        );
      }
      if (lnk.onClick) {
        return <a key={i} href="#" onClick={(e) => { e.preventDefault(); lnk.onClick(e); }}>{lnk.label}</a>;
      }
      return <a key={i} href={lnk.href}>{lnk.label}</a>;
    });

  return (
    <>
      <AnnouncementPopup />
      <div className="mnav">
        <style>{STYLES}</style>

        {/* ════════ TOP BAR ════════ */}
        <div className="mnav-topbar">
          <div style={S_FLEX_ROW_16}>
            <img src={logo} style={S_LOGO} alt="logo" />
            {companyLogo && (
              <img src={companyLogo} alt="company" style={S_COMPANY_LOGO} />
            )}
          </div>

          <div style={S_FLEX_ROW_4}>
            <a href="/managerdashboard" className="mnav-home-btn" title="Home">
              <FaHome size={20} />
            </a>

            <a href="/managersettings" className="mnav-settings-btn">
              <FaSlidersH size={15} /> Settings
            </a>

            <div style={S_DIVIDER} />

            <div style={S_RELATIVE} onMouseEnter={() => open("profile")} onMouseLeave={close}>
              <div className="mnav-profile-btn">
                <Avatar
                  photoError={photoError}
                  photo={profile.photo}
                  username={profile.username}
                  setPhotoError={setPhotoError}
                />
                <div>
                  <div className="mnav-profile-name">{profile.username}</div>
                  <div className="mnav-profile-role">{activeRole?.replace("ROLE_", "")}</div>
                </div>
                <FaChevronDown size={10} style={{
                  opacity: 0.5, marginLeft: 4, transition: "transform 0.2s",
                  transform: openDropdown === "profile" ? "rotate(180deg)" : "none"
                }} />
              </div>

              {openDropdown === "profile" && (
                <div className="mnav-profile-drop">
                  <div className="mnav-profile-header">
                    <div className="name">{profile.username}</div>
                    <span className="role-badge">{activeRole?.replace("ROLE_", "")}</span>
                  </div>

                  <a className="menu-item" href="/managerprofile">
                    <FaUserCog size={14} style={S_ICON_07} /> Profile Settings
                  </a>

                  {profile.roles?.length > 1 && (
                    <>
                      <div className="divider" />
                      <div style={S_ROLE_SECTION}>
                        <div style={S_ROLE_LABEL}>
                          <FaExchangeAlt style={S_EXCH} />Switch Role
                        </div>
                        <div style={S_ROLE_PILLS}>
                          {profile.roles.filter(r => r !== activeRole).map((role, i) => {
                            let path = "/";
                            if (role === "ROLE_MANAGER") path = "/managerdashboard";
                            if (role === "ROLE_ADMIN") path = "/admindashboard";
                            if (role === "ROLE_USER") path = "/empdashboard";
                            return (
                              <a key={i} className="role-pill" href={path}
                                onClick={e => {
                                  e.preventDefault();
                                  localStorage.setItem("activeRole", role);
                                  setProfile(p => ({ ...p, role: role.replace("ROLE_", "") }));
                                  navigate(path);
                                }}>
                                {role.replace("ROLE_", "")}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="divider" />
                  <div className="menu-item danger"
                    onClick={() => { sessionStorage.clear(); localStorage.clear(); navigate("/"); }}>
                    <FaSignOutAlt size={13} /> Logout
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ════════ MAIN NAV BAR ════════ */}
        <div className="mnav-bar" style={{ backgroundColor: navbarColor }}>
          {(() => {
            const visibleItems = navItems.filter(item => item.links.length > 0);
            const count = visibleItems.length;
            const itemStyle = count > 10
              ? { fontSize: "12px", padding: "0 8px" }
              : count > 8
                ? { fontSize: "13px", padding: "0 10px" }
                : {};
            return (
              <div style={S_NAV_FLEX}>
                {visibleItems.map(item => (
                  <div key={item.key} style={S_RELATIVE}
                    onMouseEnter={() => open(item.key)}
                    onMouseLeave={close}
                  >
                    <div
                      className={`mnav-item${openDropdown === item.key ? " active" : ""}`}
                      style={itemStyle}
                      onClick={() => navigate(item.landing)}
                    >
                      {item.label}
                      <FaChevronDown className="chevron" />
                    </div>
                    {openDropdown === item.key && (
                      <div className="mnav-drop">
                        {renderLinks(item)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          <div className="mnav-search">
            <FaSearch size={12} />
            <input
              type="text"
              placeholder="Search…"
              value={searchVal}
              onChange={e => setSearchVal(e.target.value)}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default ManagerNavbar;
